<script setup>
import {usaStates} from '@/constants/usaStates.json';
import {canadaStates} from '@/constants/canadaStates.json';

const props = defineProps({
  leadGenFields: Object,
  pageSettings: Object, 
  firstName: {
    type: String,
    default: ''
  },
  postalCode: {
    type: String,
    default: ''
  },
})
const brochurePreference = ref('online')
const selectedCountry = ref('US');
const countryList = ref([]);
const { getCountries } = useLeadGenData();

const fetchCountryList = async () => {
  try {
    const { data } = await getCountries();
    const countries = data.value;
    if (countries) {
      // Extract the list of countries from the data
      countryList.value = Object.keys(countries).map(key => {
        const country = countries[key];
        return country.attributes;
      });
    }
  } catch (error) {
    console.error('Error getting countries:', error);
  }
};
fetchCountryList();

const stateField = computed(() => {
  const result = {
    options: selectedCountry.value === 'US' ? usaStates : (selectedCountry.value === 'CA' ? canadaStates : [] ),
    placeholderText: selectedCountry.value === 'US' ? props.leadGenFields.statePlaceholderText : (selectedCountry.value === 'CA' ? 'Province/territory *' : 'State/province/region' ),
  }
  return result;
})

const emit = defineEmits(['onChanged'])
const state = ref('')

const fields = ref({
  brochureSendPrintedInformation: {
    value: false,
    valid: false
  },
  brochureFirstName: {            
    value: '',
    required: true,
    valid: false
  },
  brochureLastName: {            
    value: '',
    required: true,
    valid: false
  },
  brochureAddress1: {            
    value: '',
    required: true,
    valid: false
  },
  brochureAddress2: {            
    value: '',
    required: false,
    valid: true
  },
  brochureCity: {            
    value: '',
    required: true,
    valid: false
  },
  brochureState: {           
    value: '',
    required: true,
    valid: false
  },
  brochurePostalCode: {            
    value: '',
    required: true,
    valid: false
  },
  brochureCountry: {           
    value: '',
    required: false,
    valid: false
  },
  brochurePhoneNumber: {            
    value: '',
    required: false,
    valid: true
  }   
});

const brochureIsValid = computed(() => {
  if (brochurePreference.value === 'online') {
    return true;
  }
  else {
    for (const key in fields.value) {
      if (!fields.value[key].valid && key !== 'brochureSendPrintedInformation') {
        return false;
      }
    }
    return true;
  }
}) 



const onFieldChanged = (field) => {
  if (field.target && field.target.name === 'brochureSendPrintedInformation') {    
    fields.value[field.target.name].value = field.target.value === 'printed';
    fields.value[field.target.name].valid = true;

    // Checking if the values of the name and postal code are set so we validate them
    if (props.firstName !== '') {
      fields.value['brochureFirstName'].value = props.firstName
      fields.value['brochureFirstName'].valid = true
    }

    if (props.postalCode !== '') {
      fields.value['brochurePostalCode'].value = props.postalCode
      fields.value['brochurePostalCode'].valid = true
    }
  }
  else {
    for (const key in fields.value) {
      if (field.id === key) {
        fields.value[key].value = field.value;
        fields.value[key].valid = field.valid;
      }
    }
  }

  emit('onChanged', {
    id: 'brochure',
    valid: brochureIsValid.value,
    value: fields.value
  })
}

defineExpose({
  onFieldChanged
})


const handleCountryChange = (country) => {
  selectedCountry.value = country.value;
  onFieldChanged(country)
}

</script>

<template>
  <div class="form-heading checkbox-heading download-brochure">
    <div class="radio-title">{{ pageSettings.titleBrochureRequest }}</div>
    <div class="form-check">
      <input class="form-check-input"  type="radio" name="brochureSendPrintedInformation" id="brochurePreferenceOnline" checked v-model="brochurePreference" value="online" @change="onFieldChanged">
      <label class="form-check-label" for="brochurePreferenceOnline">
        {{ pageSettings.textDigitalBrochure }}
      </label>
    </div>
    <div class="form-check">
      <input class="form-check-input" type="radio" name="brochureSendPrintedInformation" id="brochurePreferencePrinted" v-model="brochurePreference" value="printed" @change="onFieldChanged">
      <label class="form-check-label" for="brochurePreferencePrinted">
        {{ pageSettings.textPrintedBrochure }}
      </label>
    </div>
    <div class="printed-section" v-if="brochurePreference === 'printed'">
      <p>{{ pageSettings.textAboveForm }}</p>
      <div class="row">
        <div class="col">
          <div class="radio-title">{{ pageSettings.titleBrochureAddress }}</div>
        </div>
      </div>
      <div class="row">
        <div class="col mb-3">
          <FormFloatingLabelInput :id="'brochureFirstName'" :placeholderText="leadGenFields.namePlaceholderText" :errorText="leadGenFields.nameErrorMessage" :required="true" @onChanged="onFieldChanged" :modelValue="firstName"  />
        </div>
      </div>
      <div class="row">
        <div class="col mb-3">
          <FormFloatingLabelInput :id="'brochureLastName'"  :placeholderText="leadGenFields.lastNamePlaceholderText" :errorText="leadGenFields.lastNameErrorText" :required="true" @onChanged="onFieldChanged" />
        </div>
      </div>
      <div class="row">
        <div class="col mb-3">
          <FormFloatingLabelInput :id="'brochureAddress1'"  :placeholderText="leadGenFields.mailingAddressPlaceholderText" :errorText="leadGenFields.mailingAddressErrorText" :required="true" @onChanged="onFieldChanged" />
        </div>
      </div>
      <div class="row">
        <div class="col mb-3">
          <FormFloatingLabelInput :id="'brochureAddress2'"  :placeholderText="leadGenFields.address2PlaceholderText" :errorText="leadGenFields.address2ErrorText"  @onChanged="onFieldChanged" />
        </div>
      </div>
      <div class="row">
        <div class="col mb-3">
          <FormFloatingLabelInput :id="'brochureCity'" :placeholderText="leadGenFields.cityPlaceholderText" :errorText="leadGenFields.cityErrorText" :required="true" @onChanged="onFieldChanged" />
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6 mb-3">
          <FormFieldSelect :id="'brochureState'" :options="stateField.options" :firstElementSelected="false" :placeholderText="stateField.placeholderText" :errorText="leadGenFields.stateErrorText" :required="true" v-if="selectedCountry === 'US' || selectedCountry === 'CA'" @onChanged="onFieldChanged"  />
          <FormFloatingLabelInput :id="'brochureState'" :placeholderText="stateField.placeholderText" :errorText="leadGenFields.stateErrorText" :required="true" v-if="selectedCountry !== 'US' && selectedCountry !== 'CA'" @onChanged="onFieldChanged" />
        </div>
        <div class="col-12 col-lg-6 mb-3">
          <FormFloatingLabelInput :id="'brochurePostalCode'" :placeholderText="leadGenFields.zipcodePlaceholderText" :errorText="leadGenFields.zipcodeErrorMessage" :required="true" @onChanged="onFieldChanged" :modelValue="postalCode" />
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6 mb-3">
          <FormFieldSelect :id="'brochureCountry'" :options="countryList"  :firstElementSelected="false"  :placeholderText="leadGenFields.countryPlaceholderText" :errorText="leadGenFields.countryErrorText" :required="true" @onChanged="handleCountryChange"  />
        </div>
        <div class="col-12 col-lg-6 mb-3">
          <FormFloatingLabelInput :id="'brochurePhoneNumber'" :type="'tel'" :placeholderText="leadGenFields.phonePlaceholderText" :errorText="leadGenFields.phoneErrorText" :required="false" @onChanged="onFieldChanged" />
        </div>
      </div>
    </div>
    
    
  </div>
  
</template>

<style lang="scss" scoped>
.download-brochure {
  border: 1px solid $color-gray;
  border-radius: 12px;
  padding: 20px 18px 30px 19px;
}
</style>